import { Inject, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { Doc, generateDocSearchTerms } from 'shared';

import { User } from '@angular/fire/auth';
import {
  addDoc,
  collection,
  collectionData,
  CollectionReference,
  collectionSnapshots,
  deleteDoc,
  doc,
  docSnapshots,
  DocumentReference,
  Firestore,
  getDoc,
  query,
  QueryConstraint,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  Transaction,
  updateDoc,
  writeBatch
} from '@angular/fire/firestore';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class DbService {
  private _db = inject(Firestore);
  private authenticatedAccount: User;

  constructor(private auth: AuthService, @Inject(PLATFORM_ID) private platformId: any) {
    if (isPlatformBrowser(this.platformId)) {
      this.auth.$user.subscribe((user) => {
        this.authenticatedAccount = user;
      });
    }
  }

  getAuthenticatedAccount() {
    return this.authenticatedAccount;
  }

  createId<T>(collectionName: string): { id: string; ref: DocumentReference<T> } {
    const newDocRef = doc(collection(this._db, collectionName));
    return { id: newDocRef.id, ref: newDocRef } as { id: string; ref: DocumentReference<T> };
  }

  // READY
  // Deprecated - provides parent doc capability and snapshots which adds complexity. Prefer queryCollection$()
  query$<T extends Doc>(
    collectionName: string,
    constraints: QueryConstraint[] = [],
    initializer?: (document: T) => T,
    parent?: Doc
  ): Observable<T[]> {
    const p = this.prepareParentEndpoint(parent) + collectionName;
    const col = collection(this._db, p) as CollectionReference<T>;
    const q = query(col, ...constraints);
    return collectionSnapshots(q).pipe(
      map((snaps) => {
        return snaps.map((s) => {
          const d = s.data();
          return initializer ? initializer(d) : d;
        });
      })
    );
  }

  queryCollection$<T>(
    collectionName: string,
    constraints: QueryConstraint[] = [],
    initializer?: (document: T) => T
  ): Observable<T[]> {
    const col = collection(this._db, collectionName) as CollectionReference<T>;
    const q = query(col, ...constraints);
    return collectionData<T>(q).pipe(
      map((res: T[]) => {
        return res.map((r) => {
          return initializer ? initializer(r) : r;
        });
      })
    );
  }

  doc$<T extends Doc>(path: string, initializer?: (document: T) => T, parent?: Doc): Observable<T> {
    const p = this.prepareParentEndpoint(parent) + path;
    const ref = doc(this._db, p) as DocumentReference<T>;
    // TODO: ISSUE WITH TYPESCRIPT
    return docSnapshots<T>(ref).pipe(
      map((d) => {
        return initializer ? initializer(d.data()) : d.data();
      })
    );
  }

  /// C.R.U.D. OPS

  /**
   * @param  {string} path 'collection' or 'collection/docID'
   * @param  {object} data new data
   *
   * Creates or updates data on a collection or document.
   *
   */
  async create<T extends Doc>(data: T, id?: string): Promise<T> {
    data = this.prepareData(data, id) as T;
    const path = this.getPath(data);
    const segments = path.split('/').filter((v) => v);
    try {
      if (segments.length % 2) {
        // Odd is always a collection
        console.warn('CHANGED ON MODULAR MIGRATION', path, segments, data);
        await setDoc(doc(collection(this._db, path), data.id), data, { merge: false });
      } else {
        // Even is always document
        await setDoc(doc(this._db, path), data, { merge: false });
      }
    } catch (error) {
      return Promise.reject(error);
    }
    return {
      ...(data as any),
      ...({
        createdAt: this.convertToTimestamp(data.createdAt),
        updatedAt: this.convertToTimestamp(new Date())
      } as any)
    };
  }

  read<T extends Doc>(
    key: string,
    collection: string,
    parent?: Doc,
    initializer?: (document: T) => T
  ): Promise<T> {
    const path = this.prepareParentEndpoint(parent) + collection + '/' + key;
    const ref = doc(this._db, path) as DocumentReference<T>;
    return getDoc(ref).then((snap) => {
      const d = snap.data() as T;
      return initializer ? initializer(d) : d;
    });
  }

  /**
   * @param  {string} path 'collection' or 'collection/docID'
   * @param  {object} data new data
   *
   * Creates or updates data on a collection or document.
   *
   */
  async update<T extends Doc>(data: T, merge = true): Promise<T> {
    const path = this.getPath(data);
    const segments = path.split('/').filter((v) => v);
    data = this.prepareData(data, data.id) as T;
    try {
      if (segments.length % 2) {
        // Odd is always a collection
        console.warn('CHANGED ON MODULAR MIGRATION', path, segments, data);
        const r = doc(collection(this._db, path), data.id) as DocumentReference<T>;
        await setDoc(r, data, { merge });
      } else {
        // Even is always document
        await setDoc(doc(this._db, path), data, { merge });
      }
    } catch (error) {
      return Promise.reject(error);
    }
    return data;
  }

  updatePartial(path: string, data: {}) {
    return updateDoc(doc(this._db, path), data);
  }

  getRef<T>(collectionProvided: string, path: string): DocumentReference<T> {
    const r = doc(collection(this._db, collectionProvided), path);
    return r as DocumentReference<T>;
  }

  async transaction<T>(updateFunction: (transaction: Transaction) => Promise<T>): Promise<T> {
    return runTransaction(this._db, updateFunction);
  }

  /**
   * @param  {string} path path to document
   *
   * Deletes document from Firestore
   *
   */
  delete<T extends Doc>(docc: T): Promise<void> {
    const path = this.getPath(docc);
    return deleteDoc(doc(this._db, path));
  }

  deleteWithPath(path: string): Promise<void> {
    return deleteDoc(doc(this._db, path));
  }

  async batchCreate(refCreate: Doc[]) {
    const batch = writeBatch(this._db);

    refCreate.forEach((info: Doc) => {
      const newData = this.prepareData(info, info.id);
      const p = `${info.collection}/${newData.id}`;
      batch.set(doc(this._db, p), newData, {
        merge: true
      });
    });

    try {
      await batch.commit();
    } catch (error) {
      return Promise.reject(error);
    }
    return true;
  }

  async batchUpdate(refCreate: Doc[]) {
    const batch = writeBatch(this._db);

    refCreate.forEach((info: Doc) => {
      const newData = {
        ...info,
        ...this.prepareData(info, info.id)
      };
      const p = `${info.collection}/${newData.id}`;
      batch.set(doc(this._db, p), newData, {
        merge: true
      });
    });

    return batch.commit();
  }

  /// Firebase Server Timestamp
  get timestamp() {
    return serverTimestamp();
  }

  convertToTimestamp(date: Date) {
    return new Timestamp(date.getTime() / 1000, 0);
  }

  convertTimeObjectToTimestamp(time: { seconds: number; nanoseconds: number }) {
    return new Timestamp(time.seconds, time.nanoseconds);
  }

  private prepareParentEndpoint(parent?: Doc): string {
    return parent ? this.getPath(parent) + '/' : '';
  }

  prepareData(data: Doc, id?: string): Doc {
    const timestamp = this.timestamp;
    const preppedData = {
      ...(data as any),
      id: id ? id : this.createId(data.collection).id,
      updatedAt: timestamp,
      updatedBy: this.authenticatedAccount?.uid || 'public'
    };
    if (!data.createdBy) {
      preppedData.createdBy = this.authenticatedAccount?.uid || 'public';
    }
    if (!data.createdAt) {
      preppedData.createdAt = new Date(); // breaks cursor with afs timestamp pending
    }
    return preppedData;
  }

  getPath(document: Doc): string {
    const path = '/' + document.collection + '/' + document.id;
    return document.parent ? this.getPath(document.parent) + path : path;
  }

  generateSearchTerms(input: string, extraTerms?: string[]): string[] {
    return generateDocSearchTerms(input, extraTerms);
  }
}
